import React, { useEffect, useMemo, useState } from "react";
import {
  computeRewardBarState,
  getRewardBarTrackWidthPercent,
  getTierMarkerPercentEqualSegments,
  REWARD_TIER_TYPE,
} from "../../../shared/reward-bar.js";
import { CHECKOUT_TRACKING_KEYS } from "../../../shared/checkout-tracking.js";
import {
  ensureRewardBarRuntimeContext,
  getRewardBarMatchOptions,
} from "../../utils/reward-bar-runtime";
import { buildFontFamilyCssVar, buildFontFamilyStyle } from "../../helper";
import "./reward-bar.css";

const TagIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z" />
  </svg>
);

const TruckIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M18 18.5a1.5 1.5 0 0 1-3 0 1.5 1.5 0 0 1 3 0zm-9 0a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zM20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2a3 3 0 0 0 6 0h6a3 3 0 0 0 6 0h2V8l-3-4zm-3 0V4h2.5L19 7.5V8h-2z" />
  </svg>
);

const GiftIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M20 6h-2.18A2.996 2.996 0 0 0 12.1 3.35L12 4l-.1-.65A2.996 2.996 0 0 0 6.18 6H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 12 7.4l3.38 4.6L17 10.83 14.92 8H20v6z" />
  </svg>
);

function TierIcon({ rewardType }) {
  switch (rewardType) {
    case REWARD_TIER_TYPE.FREE_SHIPPING:
      return <TruckIcon />;
    case REWARD_TIER_TYPE.PRODUCT:
      return <GiftIcon />;
    default:
      return <TagIcon />;
  }
}

const RewardBar = ({
  section,
  cart,
  formatMoney,
  embeddedInEditor = false,
  editorPartProps = {},
  isMobile = false,
}) => {
  const setting = section?.setting || {};
  const css = setting.customCSS?.[isMobile ? "mobile" : "desktop"] || {};
  const [matchReady, setMatchReady] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await ensureRewardBarRuntimeContext(setting);
      } catch (_) {}
      if (!cancelled) setMatchReady((n) => n + 1);
    })();
    return () => {
      cancelled = true;
    };
  }, [setting]);

  const state = useMemo(
    () =>
      computeRewardBarState(
        cart,
        setting,
        formatMoney,
        getRewardBarMatchOptions()
      ),
    [cart, setting, formatMoney, matchReady]
  );

  const {
    tiers,
    visualProgressPercent,
    achievedTierIds,
    headlineHtml,
    allAchieved,
  } = state;

  const hasShopperItems = useMemo(() => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    return items.some((item) => {
      const props = item?.properties;
      if (!props || typeof props !== "object") return true;
      return !props[CHECKOUT_TRACKING_KEYS.REWARD_TIER_ID];
    });
  }, [cart?.items]);

  if (
    !embeddedInEditor &&
    !setting.showOnEmptyCart &&
    !hasShopperItems
  ) {
    return null;
  }

  const barBg = setting.barBackgroundColor || "#E2E2E2";
  const barFg = setting.barForegroundColor || "#000000";
  const completeIconColor = setting.completeIconColor || "#ffffff";
  const incompleteIconColor = setting.incompleteIconColor || "#4D4949";
  const headlineBackgroundColor =
    setting.headlineBackgroundColor || "#E2E2E2";
  const completedMessageBg =
    setting.completedMessageBackgroundColor || headlineBackgroundColor;
  const trackWidthPercent = getRewardBarTrackWidthPercent(tiers.length);
  const fillPercent =
    trackWidthPercent > 0
      ? Math.min(100, Math.max(0, (visualProgressPercent / trackWidthPercent) * 100))
      : 0;

  return (
    <div
      className={`weu-reward-bar${embeddedInEditor ? " weu-reward-bar--editor weu-custom-cart-drawer__part" : ""}`}
      style={{
        ...buildFontFamilyCssVar("--weu-reward-font-family", css.fontFamily),
        "--weu-reward-font-size": css.fontSize,
        "--weu-reward-color": css.color,
        "--weu-reward-font-weight": css.fontWeight,
        "--weu-reward-text-align": css.textAlign || "center",
        "--weu-reward-desc-size":
          setting.descriptionFontSize || undefined,
        "--weu-reward-desc-color":
          setting.descriptionColor || undefined,
      }}
      {...editorPartProps}
    >
      {headlineHtml ? (
        <p
          className={`weu-reward-bar__headline${
            allAchieved ? " weu-reward-bar__headline--complete" : ""
          }`}
          style={{
            color: css.color,
            ...buildFontFamilyStyle(css.fontFamily),
            fontSize: css.fontSize,
            fontWeight: css.fontWeight || 600,
            textAlign: css.textAlign,
            backgroundColor: allAchieved
              ? completedMessageBg
              : headlineBackgroundColor,
          }}
          dangerouslySetInnerHTML={{ __html: headlineHtml }}
        />
      ) : null}

      <div
        className="weu-reward-bar__progress"
        data-tier-count={tiers.length}
        style={{ "--weu-reward-track-width": `${trackWidthPercent}%` }}
      >
        <div className="weu-reward-bar__rail">
          <div className="weu-reward-bar__track-line" aria-hidden>
            <div
              className="weu-reward-bar__track-bg"
              style={{ backgroundColor: barBg }}
            />
            <div
              className="weu-reward-bar__track-fill"
              style={{
                width: `${fillPercent}%`,
                backgroundColor: barFg,
              }}
            />
          </div>

          <div
            className={`weu-reward-bar__markers weu-reward-bar__markers--count-${tiers.length}`}
          >
            {tiers.map((tier, tierIndex) => {
              const achieved = achievedTierIds.has(tier.id);
              const markerLeft = getTierMarkerPercentEqualSegments(
                tierIndex,
                tiers.length
              );

              return (
                <div
                  key={tier.id}
                  className="weu-reward-bar__marker-col"
                  style={{ left: `${markerLeft}%` }}
                >
                  <div
                    className="weu-reward-bar__bubble"
                    style={{
                      backgroundColor: achieved ? barFg : "#F3F3F3",
                      borderColor: achieved ? barFg : "#E2E2E2",
                      color: achieved ? completeIconColor : incompleteIconColor,
                    }}
                  >
                    <TierIcon rewardType={tier.rewardType} />
                  </div>
                  <span
                    className="weu-reward-bar__label"
                    style={{
                      fontSize: setting.descriptionFontSize || undefined,
                      color: setting.descriptionColor || undefined,
                    }}
                  >
                    {tier.description || "Reward"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardBar;
